/**
 * Help chat — boundary contract schemas (help-center-chatbot DC-3, DC-4).
 *
 *   POST   /landlord/api/help/conversations                   — start (201 `{ id }`)
 *   GET    /landlord/api/help/conversations                   — list the caller's conversations
 *   GET    /landlord/api/help/conversations/:id               — `{ messages }`
 *   DELETE /landlord/api/help/conversations/:id               — delete (204)
 *   POST   /landlord/api/help/conversations/:id/messages      — post a message; the response is an SSE stream
 *   PUT    /landlord/api/help/messages/:id/rating             — rate an answered message
 *
 * The message stream (`text/event-stream`) carries two event types: `token` (`{ text }`, zero or more)
 * and exactly one terminal `final` (`{ message }`, the persisted DC-4 message). Limit and error turns
 * arrive as a `final` event (outcome `rate-limited` / `error`), never as an HTTP error; only failures
 * before the first byte (validation, auth, ownership, kill switch) are JSON HTTP errors.
 */
import { z } from 'zod';
import { isoDateTimeString } from '../shared/index.js';
/** Max length of a landlord message (BR-32). */
export const HELP_MESSAGE_MAX_CHARS = 2000;
/** Fixed text of a `rejected-input` turn (BR-32): the server sends it when a message is rejected, and the landlord composer shows it over the limit. */
export const HELP_TEXT_REJECTED_INPUT = `Messages can be up to ${HELP_MESSAGE_MAX_CHARS.toLocaleString('en-US')} characters.`;
/** Max length of the optional comment on a rating (BR-34). */
export const HELP_RATING_COMMENT_MAX_CHARS = 500;
/** `code` of the 503 returned by every chat (DC-3) route while the kill switch is off (NFR-OBSERVABILITY-6). */
export const HELP_CHAT_DISABLED_CODE = 'HELP_CHAT_DISABLED';
/** SSE event names of the message stream. */
export const HELP_STREAM_EVENT_TOKEN = 'token';
export const HELP_STREAM_EVENT_FINAL = 'final';
/** Fixed text of an `error` turn: the server sends it, and the landlord chat shows it when a request fails. */
export const HELP_TEXT_ERROR = 'Something went wrong reaching the assistant. Your message was kept.';
export const helpOutcomeSchema = z.enum([
    'answered',
    'no-answer',
    'declined-legal',
    'declined-professional',
    'action-request',
    'off-topic',
    'rate-limited',
    'rejected-input',
    'error',
]);
export const helpLimitKindSchema = z.enum(['per-minute', 'per-day', 'monthly-turns', 'monthly-tokens']);
export const helpRatingSchema = z.enum(['up', 'down']);
/** A citation is rendered from server-resolved article data, never from model output (BR-24). */
export const HelpCitationSchema = z.object({
    articleId: z.string(),
    title: z.string(),
    url: z.string(),
});
/** DC-4: one message of a help conversation. */
export const HelpBotMessageSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    role: z.enum(['user', 'assistant']),
    text: z.string(),
    outcome: helpOutcomeSchema.optional(),
    /** Empty unless the outcome is `answered` or `action-request`. */
    citations: z.array(HelpCitationSchema),
    offerEscalation: z.boolean(),
    /** Only when the outcome is `rate-limited`. */
    limitKind: helpLimitKindSchema.optional(),
    /** The caller's rating of an assistant message (BR-34). */
    rating: helpRatingSchema.optional(),
    createdAt: isoDateTimeString,
});
export const ConversationParamsSchema = z.object({ id: z.string().uuid() });
export const MessageParamsSchema = z.object({ id: z.string().uuid() });
export const ConversationCreatedSchema = z.object({ id: z.string().uuid() });
export const ConversationListItemSchema = z.object({
    id: z.string().uuid(),
    /** The first user message truncated to 60 characters; null until the first message. */
    title: z.string().nullable(),
    updatedAt: isoDateTimeString,
});
export const ConversationMessagesSchema = z.object({ messages: z.array(HelpBotMessageSchema) });
/** What the client sends: text within the limit, and the pathname the landlord is on. */
export const PostMessageBodySchema = z.object({
    text: z.string().max(HELP_MESSAGE_MAX_CHARS),
    /** Resolved to a route pattern server-side; never forwarded to the model as typed (BR-28). */
    pathname: z.string().min(1).max(2048),
});
/**
 * What the route accepts: `text` is validated by the turn pipeline, not here, so empty, over-long and
 * non-text input ends as a `rejected-input` turn without a model call (BR-32) instead of an HTTP 400.
 */
export const PostMessageRequestSchema = PostMessageBodySchema.extend({ text: z.unknown() });
export const HelpTokenEventSchema = z.object({ text: z.string() });
export const HelpFinalEventSchema = z.object({ message: HelpBotMessageSchema });
export const RatingBodySchema = z.object({
    value: helpRatingSchema,
    comment: z.string().max(HELP_RATING_COMMENT_MAX_CHARS).optional(),
});
export const RatingResponseSchema = z.object({
    rating: helpRatingSchema,
    /** True after a thumbs-down (BR-26). */
    offerEscalation: z.boolean(),
});
//# sourceMappingURL=chat.js.map